import { HOURS_IN_DAY } from '@stdlib/constants-time';
import type { RangeValue } from 'rc-picker/es/interface';

import { rangeNumbers } from './array';
import { dayjs } from './dayjs';
import { pluralize } from './strings';

export const XLSX_DATE_FORMAT = 'DD-MM-YY';

export const STANDARD_DATE_FORMAT = 'YYYY-MM-DD';

export const isInDateArray = (date: Date, days: string[]) =>
  !!days.find((d) => d === dayjs(date).format(STANDARD_DATE_FORMAT));

export const isPassed = (date: Date) =>
  dayjs().isAfter(dayjs(date)) && !dayjs().isSame(dayjs(date), 'day');

export const isBetween = (startDate: Date, endDate: Date) => {
  const today = dayjs();

  return today.isAfter(dayjs(startDate)) && today.isBefore(dayjs(endDate));
};

export const isEndOfWeek = (date: Date) => dayjs(date).day() === 0;

export const rangeValueIsNotNull = <T>(
  rangeValue: RangeValue<T>
): rangeValue is [T, T] => {
  if (rangeValue === null || rangeValue[0] === null || rangeValue[1] === null) {
    return false;
  }

  return true;
};

export const disabledStartTimesBeforeEndDate = (
  startValue: Date,
  endDate: Date
) => {
  if (dayjs(startValue).isSame(dayjs(endDate), 'day')) {
    const endHours = endDate.getHours();
    const endMinutes = endDate.getMinutes();
    const endSeconds = endDate.getSeconds();

    return {
      disabledHours: () => rangeNumbers(endHours + 1, 24),
      disabledMinutes: (hour: number) =>
        hour === endHours ? rangeNumbers(endMinutes + 1, 60) : [],
      disabledSeconds: (hour: number, minute: number) =>
        hour === endHours && minute === endMinutes
          ? rangeNumbers(endSeconds + 1, 60)
          : [],
    };
  }

  return {};
};

export const disabledEndTimesAfterStartDate = (
  endValue: Date,
  startDate: Date
) => {
  if (dayjs(endValue).isSame(dayjs(startDate), 'day')) {
    const startHours = startDate.getHours();
    const startMinutes = startDate.getMinutes();
    const startSeconds = startDate.getSeconds();

    return {
      disabledHours: () => rangeNumbers(0, startHours),
      disabledMinutes: (hour: number) =>
        hour === startHours ? rangeNumbers(0, startMinutes) : [],
      disabledSeconds: (hour: number, minute: number) =>
        hour === startHours && minute === startMinutes
          ? rangeNumbers(0, startSeconds + 1)
          : [],
    };
  }

  return {};
};

export const formatToLocalDateTime = (date: Date) => {
  return dayjs(date).format('YYYY-MM-DD[T]HH:mm:ss');
};

export const dateAndHour = (date: string) =>
  dayjs(date).format('DD/MM/YYYY [at] HH:mm');

export const formatDateWithoutHour = (date: Date | number) => {
  return dayjs(date).format(STANDARD_DATE_FORMAT);
};

export const formatTime = (time: string) => {
  return dayjs(time).format('HH:mm');
};

export const formatHoursInDays = (hours: number) => {
  if (typeof hours !== 'number' || hours < 0) {
    return 'Please provide a valid number of hours';
  }

  const days = Math.floor(hours / HOURS_IN_DAY);
  const remainingHours = hours % HOURS_IN_DAY;

  if (remainingHours === 0) {
    return pluralize(days, 'day', 'days');
  }

  return pluralize(hours, 'hour', 'hours');
};

export const sortDaysOfWeek = (days: string[]): string[] => {
  const dayOrder = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ];

  return days.sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b));
};

/** `formatter` must be a dayjs format token string. */
export const renderDate = (date: string, formatter: string) => {
  return dayjs(date).format(formatter);
};

export const formatDayMonthWithHoursMinutes = (date: string) => {
  return dayjs(date).format('DD/MM, HH:mm');
};

export const formatMonthYear = (date: string) => {
  return dayjs(date).format('MM/YY');
};

export const formatDayOfMonth = (date: string) => {
  return dayjs(date).format('Do [of] MMMM');
};

export const formatDayOfMonthWithHour = (date: string) => {
  return dayjs(date).format('Do [of] MMMM [at] HH:mm');
};

export const formatDateToDayMonthYear = (date: string) => {
  return dayjs(date).format('DD MMM YYYY');
};
